import { DcEnvironment } from '../../instance/environment';
import { DcTenant, DcTenantTld } from '../../instance/tenant';
import { appendPathToUrl, getBaseUrl, getHost, getProtocolByEnivironment } from '../utils/url-utils';

/**
 * Some Urls are not tenant specific, so we need to use the tenant tld to build the URL.
 *
 * @param prefix the prefix to use for the URL, e.g. 'api', 'www', etc.
 * @param tld the top-level domain to use, e.g. 'com', 'com.au', etc. It defaults   to 'com' as that is the most common for untenanted URLs.
 * @param hostPrefix the host prefix to use, e.g. '.dev', '.local', etc.
 * @returns a url string in the format of 'https://api.ratemyagent.com.au' or 'https://api.dev.ratemyagent.com.au'
 */
function getRmaUntenantedPublicBaseUrl(prefix: string, tld: DcTenantTld = 'com', hostPrefix?: string): string {
  const host = getHost(prefix, 'ratemyagent', tld, hostPrefix);
  const protocol = getProtocolByEnivironment('prod');
  const baseUrl = `${protocol}${host}`;
  return getBaseUrl(baseUrl);
}

/**
 * Most of the URLs are tenant and/or environment specific, so we need to use the tenant tld to build the URL.
 *
 * @param prefix the prefix to use for the URL, e.g. 'api', 'www', etc.
 * @param tenant the tenant to use for the URL, e.g. 'au', 'nz', 'us', etc.
 * @param environment the environment to use for the URL, e.g. 'prod', 'develop', 'local', etc.
 * @returns a url string in the format of 'https://api.ratemyagent.com.au' or 'https://api.dev.ratemyagent.com.au'
 */
function getRmaTenantedPublicBaseUrl(prefix: string, tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaUntenantedPublicBaseUrl(prefix, tenant.tld, environment.hostPrefix);
}

export function getRmaApiUrl(tenant: DcTenant, environment: DcEnvironment): string {
  const prefix = environment.code === 'prod' ? 'api' : 'devapi';
  return getRmaUntenantedPublicBaseUrl(prefix, tenant.tld);
}

export function getRmaBaseUrl(tenant: DcTenant, environment: DcEnvironment): string {
  const prefix = environment.code === 'prod' ? 'www' : 'dev';
  return getRmaUntenantedPublicBaseUrl(prefix, tenant.tld);
}

export function getRmaBillingApiUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('billing', tenant, environment);
}

export function getRmaCrmApiUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('crm', tenant, environment);
}

export function getRmaEmailApiUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('email', tenant, environment);
}

export function getRmaFindMyApiUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('findmyagent', tenant, environment);
}

export function getRmaMarketorApiUrl(environment: DcEnvironment): string {
  return getRmaUntenantedPublicBaseUrl('marketor', 'com', environment.hostPrefix);
}

export function getRmaReviewsApiUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('reviews', tenant, environment);
}

export function getRmaSocialApiUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('social', tenant, environment);
}

export function getRmaSurveyApiUrl(environment: DcEnvironment): string {
  return getRmaUntenantedPublicBaseUrl('surveyapi', 'com', environment.hostPrefix);
}

export function getRmaWidgetsApiUrl(environment: DcEnvironment): string {
  return getRmaUntenantedPublicBaseUrl('widgets-api', 'com', environment.hostPrefix);
}

export function getRmaTrixelsApiUrl(environment: DcEnvironment): string {
  const baseUrl = getRmaUntenantedPublicBaseUrl('trixelsanalytics', 'com.au', environment.hostPrefix);
  return appendPathToUrl('/api/', baseUrl);
}

export function getRmaIdentityUrl(environment: DcEnvironment): string {
  return getRmaUntenantedPublicBaseUrl('identity', 'com', environment.hostPrefix);
}

export function getRmaStaticUrl(tenant: DcTenant): string {
  return getRmaUntenantedPublicBaseUrl('static', tenant.tld);
}

export function getRmaStaticImageUrl(tenant: DcTenant): string {
  const baseUrl = getRmaStaticUrl(tenant);
  return appendPathToUrl('/assets/images/', baseUrl);
}

export function getRmaCdnUrl(tenant: DcTenant): string {
  return getRmaUntenantedPublicBaseUrl('cdn', tenant.tld);
}

export function getRmaSupportUrl(): string {
  return getRmaUntenantedPublicBaseUrl('support');
}

export function getRmaLanguageUrl(): string {
  return getRmaUntenantedPublicBaseUrl('language');
}
export function getRmaLandingPagesUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('landing', tenant, environment);
}

export function getRmaImagesUrl(tenant: DcTenant): string {
  return getRmaUntenantedPublicBaseUrl('fastly', tenant.tld);
}

export function getRmaDashboardUrl(tenant: DcTenant, environment: DcEnvironment): string {
  const baseUrl = getRmaBaseUrl(tenant, environment);
  return appendPathToUrl('/dashboard/', baseUrl);
}

export function getRmaPromoterUrl(tenant: DcTenant, environment: DcEnvironment): string {
  const baseUrl = getRmaBaseUrl(tenant, environment);
  return appendPathToUrl('/promoter/', baseUrl);
}

export function getSocialStudioUrl(tenant: DcTenant, environment: DcEnvironment): string {
  return getRmaTenantedPublicBaseUrl('studio', tenant, environment);
}
